// AiRequests.tsx — every AI request, and the calls inside it, across every customer.
//
// Reads GET /api/platform/usage/ai-requests, a sub-route inside
// platformAdmin.js which therefore inherits hostGate + operatorOnly
// structurally. This component adds no auth of its own and must not imply any.
//
// SCOPED TO `PT`. No import from theme.ts.
//
// A BUNDLE IS ONE INQUIRY, NOT ONE CALL. A single inquiry has spanned 52 model
// calls over about seventy seconds across two models and both the primary and
// the fallback. The row shows the total; expanding it shows every call that
// made up that total, which is the only way to see a fallback actually firing.
//
// WHAT THIS ADDS OVER THE PRODUCT'S OWN VERSION: the customer column. The
// product screen has one customer and no use for it; here it is the first thing
// an operator needs.
//
// COLLAPSED AND CAPPED BY DEFAULT. This is the longest thing on the screen and
// it is evidence, not the headline. Truncation is always stated — a list that
// quietly stops reads as "that is everything", which is the one thing it is not.

import React, { useCallback, useEffect, useState } from "react";
import { apiCallEx } from "../../apiClient";
import { PT, PT_NUM, fmtCost, fmtNum, fmtMs, fmtTokens } from "../../platformTheme";

interface Call {
  at: string; provider: string; model: string; tier: string | null; role: string | null;
  input_tokens: number; output_tokens: number; latency_ms: number | null;
  outcome: string; error_class?: string | null; stop_reason?: string | null;
  cost: number | null; priced: boolean;
  tenant_name?: string | null;
}
interface Bundle {
  request_id: string | null; unattributed: boolean;
  calls: Call[]; call_count: number;
  started_at: string; ended_at: string; duration_ms: number;
  tiers: (string | null)[]; models: string[];
  used_fallback: boolean; used_tertiary: boolean; failures: number;
  cost: number; cost_is_partial: boolean; unpriced_calls: number;
  tenant_name?: string | null; tenant_ambiguous?: boolean;
}
interface Payload {
  totals: { bundles: number; calls: number; cost: number; cost_is_partial: boolean; unpriced_calls: number };
  bundles: Bundle[];
  truncated: boolean;
  scope: "all_tenants" | "single_customer";
  partial: boolean;
  tenants_failed: { tenant_name: string; error: string }[];
  unpriced_models: string[];
}

const card: React.CSSProperties = {
  background: PT.bg2, border: `1px solid ${PT.brd}`, borderRadius: PT.radiusLg, padding: 0,
};
const th: React.CSSProperties = {
  textAlign: "left", fontSize: 10, fontWeight: 600, letterSpacing: 0.5,
  textTransform: "uppercase", color: PT.txD, padding: "8px 10px",
  borderBottom: `1px solid ${PT.brd}`, whiteSpace: "nowrap",
};
const td: React.CSSProperties = {
  fontSize: 12.5, padding: "9px 10px",
  borderBottom: `1px solid ${PT.brdSubtle}`, whiteSpace: "nowrap",
};

const PREVIEW = 25;

const Caret: React.FC<{ open: boolean }> = ({ open }) => (
  <span style={{
    display: "inline-block", width: 12, color: PT.txD,
    transform: open ? "rotate(90deg)" : "none", transition: "transform .12s",
  }}>&#9656;</span>
);

const when = (s: string) => {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

/** Which model answered, and whether it was the one we meant to use. */
const roleBadge = (role: string | null) => {
  if (!role || role === "primary") return null;
  const c = role === "fallback" ? PT.warn : PT.err;
  return (
    <span style={{
      marginLeft: 6, fontSize: 10, fontWeight: 600, textTransform: "uppercase",
      color: c, border: `1px solid ${c}`, borderRadius: 4, padding: "1px 4px",
    }}>{role}</span>
  );
};

const AiRequests: React.FC<{ days: number }> = ({ days }) => {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [show, setShow] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    const r = await apiCallEx(`/platform/usage/ai-requests?days=${days}`);
    if (r.ok && r.data) { setData(r.data as Payload); setErr(null); } else {
      setData(null);
      setErr((r.data as { error?: string })?.error || `Could not read AI requests (HTTP ${r.status}).`);
    }
  }, [days]);

  useEffect(() => { void load(); }, [load]);

  const bundles = data ? (showAll ? data.bundles : data.bundles.slice(0, PREVIEW)) : [];

  return (
    <div style={{ ...card, overflowX: "auto" }}>
      <div
        onClick={() => setShow(!show)}
        style={{
          padding: "12px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
          borderBottom: show ? `1px solid ${PT.brd}` : "none", userSelect: "none",
        }}
      >
        <Caret open={show} /> Every request in detail · {days}d
        {data && (
          <span style={{ fontWeight: 400, color: PT.txM, marginLeft: 8 }}>
            {fmtNum(data.totals.bundles)} request{data.totals.bundles === 1 ? "" : "s"},{" "}
            {fmtNum(data.totals.calls)} model call{data.totals.calls === 1 ? "" : "s"} — each inquiry, the
            calls it made, whether it fell back, and what each one cost
          </span>
        )}
        {err && <span style={{ fontWeight: 400, color: PT.err, marginLeft: 8 }}>{err}</span>}
      </div>

      {show && data && (
        <>
          {(data.partial || data.truncated || data.totals.cost_is_partial) && (
            <div style={{ padding: "10px 16px", fontSize: 11.5, color: PT.warn, borderBottom: `1px solid ${PT.brdSubtle}` }}>
              {data.partial && (
                <>Incomplete: {data.tenants_failed.map((t) => t.tenant_name).join(", ")} could not be read.{" "}</>
              )}
              {data.totals.cost_is_partial && (
                <>{data.totals.unpriced_calls} call{data.totals.unpriced_calls === 1 ? "" : "s"} have no price on file
                  {data.unpriced_models.length > 0 && <> ({data.unpriced_models.join(", ")})</>}, so the total is a floor.{" "}</>
              )}
              {data.truncated && <>Only the most recent calls in this window were read.</>}
            </div>
          )}

          {data.bundles.length === 0 ? (
            <div style={{ padding: 16, fontSize: 12.5, color: PT.txM }}>No AI requests in this window.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th}>When</th>
                  {data.scope === "all_tenants" && <th style={th}>Customer</th>}
                  <th style={{ ...th, textAlign: "right" }}>Cost</th>
                  <th style={{ ...th, textAlign: "right" }}>Calls</th>
                  <th style={{ ...th, textAlign: "right" }}>Took</th>
                  <th style={th}>Models</th>
                  <th style={th}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {bundles.map((b) => {
                  const key = b.request_id || b.started_at;
                  const isOpen = !!open[key];
                  return (
                    <React.Fragment key={key}>
                      <tr onClick={() => setOpen({ ...open, [key]: !isOpen })} style={{ cursor: "pointer" }}>
                        <td style={{ ...td, color: PT.txM }}><Caret open={isOpen} /> {when(b.started_at)}</td>
                        {data.scope === "all_tenants" && (
                          <td style={{ ...td, fontWeight: 500 }}>
                            {b.tenant_name || <span style={{ color: PT.txD }}>unattributed</span>}
                            {b.tenant_ambiguous && <span style={{ color: PT.err }}> (spans customers)</span>}
                          </td>
                        )}
                        <td style={{ ...td, ...PT_NUM, textAlign: "right", fontWeight: 600 }}>
                          {fmtCost(b.cost)}
                          {b.cost_is_partial && <span style={{ color: PT.warn }}> +?</span>}
                        </td>
                        <td style={{ ...td, ...PT_NUM, textAlign: "right" }}>{b.call_count}</td>
                        <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtMs(b.duration_ms)}</td>
                        <td style={{ ...td, fontSize: 11.5, color: PT.txM }}>{b.models.join(", ")}</td>
                        <td style={{ ...td, fontSize: 11.5 }}>
                          {/* The reason this view exists: a fallback firing is
                              invisible in any total. */}
                          {b.used_fallback && <span style={{ color: PT.warn }}>fell back</span>}
                          {b.used_tertiary && <span style={{ color: PT.err, marginLeft: 6 }}>third model</span>}
                          {b.failures > 0 && <span style={{ color: PT.err, marginLeft: 6 }}>{b.failures} failed</span>}
                          {b.unattributed && <span style={{ color: PT.txD, marginLeft: 6 }}>no request id</span>}
                        </td>
                      </tr>

                      {isOpen && (
                        <tr>
                          <td style={{ ...td, padding: 0 }} colSpan={data.scope === "all_tenants" ? 7 : 6}>
                            <div style={{ overflowX: "auto", background: PT.bg }}>
                              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                <thead>
                                  <tr>
                                    <th style={{ ...th, paddingLeft: 34 }}>Call</th>
                                    <th style={th}>Model</th>
                                    <th style={th}>Tier</th>
                                    <th style={{ ...th, textAlign: "right" }}>In</th>
                                    <th style={{ ...th, textAlign: "right" }}>Out</th>
                                    <th style={{ ...th, textAlign: "right" }}>Latency</th>
                                    <th style={th}>Outcome</th>
                                    <th style={{ ...th, textAlign: "right" }}>Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {b.calls.map((c, i) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <tr key={`${c.at}-${i}`}>
                                      <td style={{ ...td, paddingLeft: 34, color: PT.txM }}>{when(c.at)}</td>
                                      <td style={{ ...td }}>
                                        {c.provider}/{c.model}{roleBadge(c.role)}
                                      </td>
                                      <td style={{ ...td, color: PT.txM }}>{c.tier || "—"}</td>
                                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(c.input_tokens)}</td>
                                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtTokens(c.output_tokens)}</td>
                                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: PT.txM }}>{fmtMs(c.latency_ms)}</td>
                                      <td style={{ ...td, color: c.outcome === "success" ? PT.txM : PT.err }}>
                                        {c.outcome}{c.error_class ? ` · ${c.error_class}` : ""}
                                      </td>
                                      <td style={{ ...td, ...PT_NUM, textAlign: "right", color: c.priced ? undefined : PT.warn }}>
                                        {c.priced ? fmtCost(c.cost) : "not priced"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Never a silent truncation. */}
          {!showAll && data.bundles.length > PREVIEW && (
            <div style={{ padding: "10px 16px", borderTop: `1px solid ${PT.brdSubtle}` }}>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                style={{
                  background: "none", border: `1px solid ${PT.brd}`, borderRadius: PT.radius,
                  color: PT.tx, fontSize: 12, padding: "5px 10px", cursor: "pointer",
                }}
              >
                Showing the {PREVIEW} most recent of {fmtNum(data.bundles.length)} — show all
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AiRequests;
